<template>
  <div class="ex-checkpoint-action-bar">
    <div class="ex-right-bar">
      <CheckpointLevelBadge :level="level" />
      <StandardButton
        type="magenta"
        variant="primary"
        size="small"
        :label="label"
        @click="onLearnMoreClick"
      />
      <WatchVideoButton
        v-if="video"
        :label="'Watch Video'"
        class="ins-watch-video"
        @onClick="handleWatchVideo"
      />
    </div>
    <div class="ex-left-bar">
      <div
        v-if="showChatter"
        @click="openChatter(checkpointInfo)"
        class="chatter-type-list"
      >
        <v-img
          class="chatter-icon"
          :src="require('@components/assets/ChatterIcon.svg')"
        />
      </div>
      <CheckpointInspectionStatusBadge
        :checkpoint_status="inspection_status"
        :inspected_by_avatar="userInfo.inspected_by_avatar"
        :inspected_by="userInfo.inspected_by"
        :inspected_on="userInfo.inspected_on"
        :reason="reason"
        :prefix="prefix"
      />
    </div>
    <ChatterDrawer
      ref="drawerContainer"
      :drawer="drawer"
      :avatar="avatar"
      :isEmployeeChatter="true"
      :type="typeName"
      :typeValue="typeValue"
      :listData="listData"
      @closeDrawer="closeDrawer($event)"
      :chatterList="chatterData"
      :totalCheckpointChatter="totalChatterData"
      @requestMoreCheckpointsChatter="
        $emit('requestMoreCheckpointsChatter', $event)
      "
    />

    <v-dialog
      @click:outside="closeVideoDialog"
      v-model="showVideoPlayer"
      max-width="680px"
      class="video-dialog"
    >
      <v-card class="video-card">
        <v-card-title class="video-title">
          <span class="title-text">Watch Video</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="closeVideoDialog"
            class="close-btn"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="video-content p-3">
          <div
            v-if="videoContent"
            v-html="videoContent"
            class="video-iframe-container"
          ></div>
          <div v-else class="no-content">
            <v-icon size="64" color="grey">mdi-video-off</v-icon>
            <p>No video content available</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref } from "vue";
import CheckpointLevelBadge from "@components/CheckpointLevelBadge/CheckpointLevelBadge.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import WatchVideoButton from "@components/WatchVideoButton/WatchVideoButton.vue";
import CheckpointInspectionStatusBadge from "@components/CheckpointInspectionStatusBadge/CheckpointInspectionStatusBadge.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
// Props definition
const props = defineProps({
  level: {
    type: String,
    validator: function (value) {
      return (
        ["Apprentice", "Professional", "Coach", "Master"].indexOf(value) !== -1
      );
    },
  },
  userInfo: {
    type: Object,
    default: () => ({}),
  },
  instructions: {
    type: String,
    default: null,
  },
  video: {
    type: String,
    default: null,
  },
  inspection_status: {
    type: String,
    default: "Pending",
  },
  reason: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "Learn More",
  },
  checkpointInfo: {
    type: Object,
    default: () => ({}),
  },
  showChatter: {
    type: Boolean,
    default: false,
  },
  prefix: {
    type: String,
    default: null
  },
});

const emit = defineEmits(["chatter-action", "learn-more-click"]);
const drawer = ref(false);
const avatar = ref({});
const typeName = ref("");
const typeValue = ref("");
const listData = ref([]);
const chatterData = ref([]);
const totalChatterData = ref(0);
const showVideoPlayer = ref(false);
const videoContent = ref(null);
const openChatter = (data) => {
  listData.value = data;
  typeValue.value = data.criteria;
  typeName.value = "Checkpoint";
  drawer.value = true;
  avatar.value.picture = data.userInfo.inspected_by_avatar;
  avatar.value.firstName = data.userInfo.inspected_by;
  emit("chatter-action", data.data);
};
const closeDrawer = (data) => {
  if (data) {
    drawer.value = false;
  }
};

const onLearnMoreClick = () => {
  const checkpointData = {
    prefix: props.prefix,
    video_url: props.video,
    status: props.inspection_status,
    readiness_level: props.level,
    criteria: props.checkpointInfo?.criteria || "",
    instructions: props.instructions,
    inspector_info: {
      inspected_by: props.userInfo?.inspected_by || "",
      inspected_by_avatar: props.userInfo?.inspected_by_avatar || "",
      inspected_on: props.userInfo?.inspected_on || "",
      reason: props.reason || "",
      status: props.inspection_status
    },
    responsibility: props.checkpointInfo?.responsibility || "",
    role: props.checkpointInfo?.role || "",
    position: props.checkpointInfo?.position || "",
    ...props.checkpointInfo
  };
  emit("learn-more-click", checkpointData);
};

const handleWatchVideo = () => {
  if (!props.video) return;
  
  const isProcessing = props.video && (
    props.video.includes("being processed") || 
    props.video.includes("will be available in a moment") ||
    props.video.includes("processing")
  );
  
  if (isProcessing) {
    return;
  }
  
  videoContent.value = props.video;
  showVideoPlayer.value = true;
};

const closeVideoDialog = () => {
  showVideoPlayer.value = false;
  videoContent.value = null;
};
</script>

<style>
.ex-checkpoint-action-bar {
  display: flex;
  justify-content: space-between;
}
.ex-right-bar {
  display: flex;
  align-items: center;
  gap: 11px;
}
.ex-left-bar {
  display: flex;
  align-items: center;
}
.ex-right-bar .watch-video {
  margin: 0px 0px 0px -5px !important;
}
.chatter-type-list {
  display: grid;
  justify-content: end;
}
.chatter-type-list {
  padding: 6px;
  text-align: center;
  margin: 5px;
  margin-top: 4px;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
}
.chatter-type-list:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  margin: 5px;
  margin-top: 4px;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}
.ins-btn {
  height: 37px;
}
.ins-watch-video .video-btn {
  height: 37px;
  font-size: 12px;
  padding: 15px;
  border: 1px solid #d0d5dd !important;
  box-shadow: 0px 1px 2px 0px #1018280d !important;
  gap: 8px;
}
.ins-watch-video .video-btn:hover {
  background-color: #f5f3f3;
}
.ins-watch-video:hover {
  box-shadow: none !important;
  background-color: transparent !important;
}
.ins-watch-video .video-avatar-container {
  width: 20px;
  height: 20px;
}

.video-dialog {
  border-radius: 12px;
}

.video-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.video-title {
  background-color: #f9fafb;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.video-title .title-text {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.video-title .close-btn {
  color: #667085;
}

.video-title .close-btn:hover {
  background-color: #f2f4f7;
}

.video-content {
  padding: 24px;
  background-color: #ffffff;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.video-iframe-container {
  width: 100%;
  height: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.video-iframe-container iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.no-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #667085;
  font-family: Inter;
  font-size: 16px;
  gap: 16px;
}

.no-content p {
  margin: 0;
  font-weight: 500;
}
</style>
